import * as L from 'leaflet';

/**
 * The pulsing "active vessel" selection ring — ONE Leaflet-level
 * implementation shared by every AIS provider's own selection concept
 * (Voyage Radar's `SelectionController`, VesselFinder's `VfLocatedLayer`, and
 * any future provider that gains a notion of "the vessel the user picked").
 * The glyph, its pane and its lifecycle used to be Voyage Radar's alone;
 * every provider now draws the identical ring rather than re-implementing it.
 *
 * Deliberately NOT part of `AisMapContext`/`SelectionPort` — those carry a
 * provider's own selection RULES (track state, spend, popup wiring), which
 * differ per operator and stay where they are. This class is only the
 * rendering primitive underneath them: a marker, a pane, and the "was I swept
 * off the map by a full-layer reset" recovery every caller needs.
 *
 * The CSS itself (`.vr-halo`, `.vr-halo__ring`, `.vr-halo__pulse`,
 * `@keyframes vr-halo-pulse`) is global in `client/src/styles.scss` — Leaflet
 * mounts marker DOM outside any component's view, so it has to be.
 */
export class ActiveVesselHalo {
  private _markers = new Map<string, L.Marker>();

  /** The halo's first marker, or null with nothing selected — read by a caller that rides it (e.g. a track replay frame). */
  get marker(): L.Marker | null {
    return this._markers.values().next().value ?? null;
  }

  /** Creates/moves the ring onto `latlng`, tinted `color`. */
  moveTo(map: L.Map, latlng: L.LatLngExpression, color: string, key: string = '__single__'): void {
    let m = this._markers.get(key);
    if (m && !map.hasLayer(m)) {
      // A full-layer sweep (`resetMapNew()`, task switches/scene changes) can
      // take this off the map without telling this class — re-add rather
      // than trust the reference is still attached.
      map.addLayer(m);
    }
    if (!m) {
      m = L.marker(latlng, {
        icon: L.divIcon({
          className: 'vr-halo',
          html: `<span class="vr-halo__ring"></span><span class="vr-halo__pulse"></span>`,
          iconSize: [72, 72],
          iconAnchor: [36, 36],
        }),
        interactive: false,
        keyboard: false,
        pane: this.pane(map),
      } as any).addTo(map);
      this._markers.set(key, m);
    } else {
      m.setLatLng(latlng);
    }
    m.getElement()?.style?.setProperty('--vr-halo-color', color);
  }

  /** Syncs multiple rings at once, creating/moving markers for keys in positions and removing any others. */
  syncMultiple(map: L.Map, positions: Map<string, L.LatLngExpression>, color: string): void {
    for (const [k, marker] of Array.from(this._markers.entries())) {
      if (!positions.has(k)) {
        map.removeLayer(marker);
        this._markers.delete(k);
      }
    }
    positions.forEach((latlng, k) => {
      this.moveTo(map, latlng, color, k);
    });
  }

  /** Takes the ring(s) off the map without touching the selection it belongs to — the caller's own concern. */
  remove(map: L.Map | null | undefined, key?: string): void {
    if (key) {
      const m = this._markers.get(key);
      if (m) {
        map?.removeLayer(m);
        this._markers.delete(key);
      }
      return;
    }
    for (const m of this._markers.values()) {
      map?.removeLayer(m);
    }
    this._markers.clear();
  }

  /** Nulls the reference WITHOUT touching the map — for a reset that has already swept every layer off it. */
  dropRef(): void {
    this._markers.clear();
  }

  /**
   * The ring's own pane, created lazily since a caller's map may not exist
   * yet when it is built. z-index 500: above track trails (canvas renderer,
   * `overlayPane`, 400), below every vessel glyph (`leaflet-marker-pane`,
   * lifted to 2700 by the same stylesheet). `pointer-events: none` because
   * the ring is 72px across and would otherwise swallow clicks aimed at the
   * water, and at the vessel itself once the halo is wider than the glyph.
   */
  private pane(map: L.Map): string {
    const name = 'vrHaloPane';
    if (!map.getPane(name)) {
      const pane = map.createPane(name);
      pane.style.zIndex = '500';
      pane.style.pointerEvents = 'none';
    }
    return name;
  }
}
